import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import refractor from 'refractor/core';
import { bool, isObject, isString, findParentNodeOfType, isEqual, object, flattenArray, findChildrenByNode, getChangedNodes, extensionDecorator, mod, NodeExtension, ExtensionTag, isElementDomNode, toggleBlockItem, setBlockType, nodeInputRule, findNodeAtSelection, isNodeOfType, replaceNodeAtPosition, removeNodeAtPosition, isTextSelection, isNodeActive, getMatchString } from '@remirror/core';
import { keydownHandler } from '@remirror/pm/keymap';
import { TextSelection } from '@remirror/pm/state';
import _classPrivateFieldGet from '@babel/runtime/helpers/esm/classPrivateFieldGet';
import _classPrivateFieldSet from '@babel/runtime/helpers/esm/classPrivateFieldSet';
import { Decoration, DecorationSet } from '@remirror/pm/view';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';

var dataAttribute = 'data-code-block-language';

/**
 * Maps the refractor nodes into text and classes which will be used to create
 * our decoration.
 */
function parseRefractorNodes(refractorNodes, plainTextClassName) {
  var className = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
  return refractorNodes.map(node => {
    var classes = [...className];

    if (node.type === 'element' && node.properties.className) {
      classes.push(...node.properties.className);
    } else if (node.type === 'text' && classes.length === 0 && plainTextClassName) {
      classes.push(plainTextClassName);
    }

    if (node.type === 'element') {
      return parseRefractorNodes(node.children, plainTextClassName, classes);
    }

    return {
      text: node.value,
      classes
    };
  });
}

/**
 * Retrieves positioned refractor nodes from the positionedNode
 *
 * @param nodeWithPosition - a node and position
 * @param plainTextClassName - a class to assign to text nodes on the top-level
 * @returns the positioned refractor nodes which are text, classes and a FromTo
 * interface
 */
function getPositionedRefractorNodes(parameter, plainTextClassName) {
  var _node$textContent, _node$attrs$language;

  var node = parameter.node,
      pos = parameter.pos;
  var refractorNodes = refractor.highlight((_node$textContent = node.textContent) !== null && _node$textContent !== void 0 ? _node$textContent : '', (_node$attrs$language = node.attrs.language) !== null && _node$attrs$language !== void 0 ? _node$attrs$language : 'markup');
  var parsedRefractorNodes = parseRefractorNodes(refractorNodes, plainTextClassName);
  var startPos = pos + 1;

  function mapper(refractorNode) {
    var from = startPos;
    var to = from + refractorNode.text.length;
    startPos = to;
    return _objectSpread(_objectSpread({}, refractorNode), {}, {
      from,
      to
    });
  }

  return flattenArray(parsedRefractorNodes).map(mapper);
}
/**
 * Creates a decoration set for the provided blocks
 */


function createDecorations(parameter) {
  var blocks = parameter.blocks,
      skipLast = parameter.skipLast,
      plainTextClassName = parameter.plainTextClassName;
  var decorations = [];

  var _iterator = _createForOfIteratorHelper(blocks),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var block = _step.value;
      var positionedRefractorNodes = getPositionedRefractorNodes(block, plainTextClassName);
      var lastBlockLength = skipLast ? positionedRefractorNodes.length - 1 : positionedRefractorNodes.length;

      for (var index = 0; index < lastBlockLength; index++) {
        var positionedRefractorNode = positionedRefractorNodes[index];
        var classes = positionedRefractorNode.classes;

        if (classes.length === 0) {
          // Do not create a decoration if we cannot assign at least one class
          continue;
        }

        var decoration = Decoration.inline(positionedRefractorNode.from, positionedRefractorNode.to, {
          class: classes.join(' ')
        });
        decorations.push(decoration);
      }
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  return decorations;
}
/**
 * Check that the attributes exist and are valid for the codeBlock
 * updateAttributes.
 */

function isValidCodeBlockAttributes(attributes) {
  return bool(attributes && isObject(attributes) && isString(attributes.language) && attributes.language.length);
}
/**
 * Updates the node attrs.
 *
 * This is used to update the language for the codeBlock.
 */

function updateNodeAttributes(type) {
  return attributes => (_ref) => {
    var _ref$state = _ref.state,
        tr = _ref$state.tr,
        selection = _ref$state.selection,
        dispatch = _ref.dispatch;

    if (!isValidCodeBlockAttributes(attributes)) {
      throw new Error('Invalid attrs passed to the updateAttributes method');
    }

    var parent = findParentNodeOfType({
      types: type,
      selection
    });

    if (!parent || isEqual(attributes, parent.node.attrs)) {
      // Do nothing since the attrs are the same
      return false;
    }

    tr.setNodeMarkup(parent.pos, type, _objectSpread(_objectSpread({}, parent.node.attrs), attributes));

    if (dispatch) {
      dispatch(tr);
    }

    return true;
  };
}

/**
 * Get the language from user input.
 */
function getLanguage(parameter) {
  var language = parameter.language,
      fallback = parameter.fallback;

  if (!language) {
    return fallback;
  }

  var supportedLanguages = refractor.listLanguages();

  var _iterator2 = _createForOfIteratorHelper(supportedLanguages),
      _step2;

  try {
    for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
      var name = _step2.value;

      if (name.toLowerCase() === language.toLowerCase()) {
        return name;
      }
    }
  } catch (err) {
    _iterator2.e(err);
  } finally {
    _iterator2.f();
  }

  return fallback;
}
/**
 * Used to provide a `toDom` function for the code block. Currently this only
 * support the browser runtime.
 */

function codeBlockToDOM(node, toDOM) {
  var _ref2 = node.attrs,
      language = _ref2.language,
      rest = _objectWithoutProperties(_ref2, ["language"]);

  var attributes = _objectSpread(_objectSpread(_objectSpread({}, toDOM(node)), rest), {}, {
    class: "language-".concat(language)
  });

  return ['pre', attributes, ['code', {
    [dataAttribute]: language
  }, 0]];
}

/**
 * A factory for creating a command which can format a selected codeBlock (or
 * one located at the provided position).
 */
function formatCodeBlockFactory(parameter) {
  return function () {
    var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : object(),
        pos = _ref3.pos;

    return (_ref4) => {
      var tr = _ref4.tr,
          dispatch = _ref4.dispatch;
      var type = parameter.type,
          formatter = parameter.formatter,
          fallback = parameter.defaultLanguage;
      var selection = tr.selection;

      var _ref5 = pos ? {
        from: pos,
        to: pos
      } : selection,
          from = _ref5.from,
          to = _ref5.to; // Find the current codeBlock the cursor is positioned in.


      var codeBlock = findParentNodeOfType({
        types: type,
        selection
      });

      if (!codeBlock) {
        return false;
      } // Get the `language`, `source` and `cursorOffset` for the block and run the
      // formatter


      var _codeBlock$node = codeBlock.node,
          attrs = _codeBlock$node.attrs,
          textContent = _codeBlock$node.textContent,
          start = codeBlock.start;
      var offsetStart = from - start;
      var offsetEnd = to - start;
      var language = getLanguage({
        language: attrs.language,
        fallback
      });
      var formatStart = formatter({
        source: textContent,
        language,
        cursorOffset: offsetStart
      });
      var formatEnd; // When the user has a selection

      if (offsetStart !== offsetEnd) {
        formatEnd = formatter({
          source: textContent,
          language,
          cursorOffset: offsetEnd
        });
      }

      if (!formatStart) {
        return false;
      }

      var cursorOffset = formatStart.cursorOffset,
          formatted = formatStart.formatted; // Do nothing if nothing has changed

      if (formatted === textContent) {
        return false;
      }

      var end = start + textContent.length; // Replace the codeBlock content with the transformed text.

      tr.insertText(formatted, start, end); // Set the new selection

      var anchor = start + cursorOffset;
      var head = formatEnd ? start + formatEnd.cursorOffset : undefined;
      tr.setSelection(TextSelection.create(tr.doc, anchor, head));

      if (dispatch) {
        dispatch(tr);
      }

      return true;
    };
  };
}

var _extension = /*#__PURE__*/new WeakMap();

var _type = /*#__PURE__*/new WeakMap();

var _deleted = /*#__PURE__*/new WeakMap();

class CodeBlockState {
  /**
   * Keep track of the node type of the `codeBlock`
   */

  /**
   * Tracks whether or not a deletion has occurred.
   */
  constructor(type, extension) {
    _extension.set(this, {
      writable: true,
      value: void 0
    });

    _type.set(this, {
      writable: true,
      value: void 0
    });

    _deleted.set(this, {
      writable: true,
      value: false
    });

    _classPrivateFieldSet(this, _type, type);

    _classPrivateFieldSet(this, _extension, extension);
  }
  /**
   * Creates the initial set of decorations
   */


  init(state) {
    // Find all the `codeBlocks` in the editor.
    var blocks = findChildrenByNode({
      node: state.doc,
      type: _classPrivateFieldGet(this, _type)
    }); // Update the `codeBlocks` with the relevant syntax highlighting.

    this.refreshDecorationSet(state.doc, blocks);
    return this;
  }
  /**
   * Recreate all the decorations again for all the provided blocks.
   */


  refreshDecorationSet(doc, blocks) {
    var _classPrivateFieldGet2;

    var decorations = createDecorations({
      blocks,
      skipLast: _classPrivateFieldGet(this, _deleted),
      defaultLanguage: _classPrivateFieldGet(this, _extension).options.defaultLanguage,
      plainTextClassName: (_classPrivateFieldGet2 = _classPrivateFieldGet(this, _extension).options.plainTextClassName) !== null && _classPrivateFieldGet2 !== void 0 ? _classPrivateFieldGet2 : undefined
    }); // Store the decoration set to be applied to the editor by the plugin.

    this.decorationSet = DecorationSet.create(doc, decorations);
  }
  /**
   * Apply the state and update decorations when a change has happened in the
   * editor.
   */


  apply(tr) {
    if (!tr.docChanged) {
      return this;
    }

    this.decorationSet = this.decorationSet.map(tr.mapping, tr.doc); // I was thinking I would need to check for deletions, but, since the
    // `decorationSet` is mapped over the previous change all decorations from a
    // deleted node will automatically be deleted. All that is needed is to find
    // the changed nodes and update their decorations.

    var changedNodes = getChangedNodes(tr, {
      descend: true,
      predicate: node => node.type === _classPrivateFieldGet(this, _type),
      StepTypes: []
    });
    this.updateDecorationSet(tr, changedNodes);
    return this;
  }
  /**
   * Removes all decorations which relate to the changed block node before creating new decorations
   * and adding them to the decorationSet.
   */


  updateDecorationSet(tr, blocks) {
    var _classPrivateFieldGet3;

    if (blocks.length === 0) {
      return;
    }

    var decorationSet = this.decorationSet;

    var _iterator = _createForOfIteratorHelper(blocks),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _step$value = _step.value,
            node = _step$value.node,
            pos = _step$value.pos;
        decorationSet = this.decorationSet.remove(this.decorationSet.find(pos, pos + node.nodeSize));
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    this.decorationSet = decorationSet.add(tr.doc, createDecorations({
      blocks,
      skipLast: _classPrivateFieldGet(this, _deleted),
      defaultLanguage: _classPrivateFieldGet(this, _extension).options.defaultLanguage,
      plainTextClassName: (_classPrivateFieldGet3 = _classPrivateFieldGet(this, _extension).options.plainTextClassName) !== null && _classPrivateFieldGet3 !== void 0 ? _classPrivateFieldGet3 : undefined
    }));
  }
  /**
   * Flags that a deletion has just occurred.
   */


  setDeleted(deleted) {
    _classPrivateFieldSet(this, _deleted, deleted);
  }

}

var _dec, _class, _temp;
var CodeBlockExtension = (_dec = /*#__PURE__*/extensionDecorator({
  defaultOptions: {
    supportedLanguages: [],
    keyboardShortcut: /*#__PURE__*/mod('ShiftAlt', 'f'),
    toggleName: 'paragraph',
    formatter: (_ref) => {
      var source = _ref.source;
      return {
        cursorOffset: 0,
        formatted: source
      };
    },
    syntaxTheme: 'atomDark',
    defaultLanguage: 'markup',
    // See https://github.com/remirror/remirror/issues/624 for the ''
    plainTextClassName: ''
  }
}), /*#__PURE__*/_dec(_class = (_temp = class CodeBlockExtension extends NodeExtension {
  constructor() {
    super(...arguments);

    _defineProperty(this, "tags", [ExtensionTag.BlockNode, ExtensionTag.Code]);
  }

  get name() {
    return 'codeBlock';
  }

  /**
   * Add the languages to the environment if they have not yet been added.
   */
  init() {
    this.registerLanguages();
  }

  createNodeSpec(extra) {
    return {
      attrs: _objectSpread(_objectSpread({}, extra.defaults()), {}, {
        language: {
          default: this.options.defaultLanguage
        }
      }),
      content: 'text*',
      marks: '',
      code: true,
      defining: true,
      isolating: true,
      draggable: false,
      parseDOM: [{
        tag: 'pre',
        preserveWhitespace: 'full',
        getAttrs: node => {
          if (!isElementDomNode(node)) {
            return false;
          }

          var codeElement = node.querySelector('code');

          if (!isElementDomNode(codeElement)) {
            return false;
          }

          var language = codeElement.getAttribute(dataAttribute);
          return _objectSpread(_objectSpread({}, extra.parse(node)), {}, {
            language
          });
        }
      }],
      toDOM: node => codeBlockToDOM(node, extra.dom)
    };
  }

  createCommands() {
    return {
      /**
       * Call this method to toggle the code block.
       *
       * @remarks
       *
       * ```ts
       * actions.toggleCodeBlock({ language: 'ts' });
       * ```
       *
       * The above makes the current node a codeBlock with the language ts or
       * remove the code block altogether.
       */
      toggleCodeBlock: attributes => toggleBlockItem({
        type: this.type,
        toggleType: this.store.schema.nodes[this.options.toggleName],
        attrs: _objectSpread({
          language: this.options.defaultLanguage
        }, attributes)
      }),

      /**
       * Creates a code at the current position.
       *
       * ```ts
       * commands.createCodeBlock({ language: 'js' });
       * ```
       */
      createCodeBlock: attributes => {
        return setBlockType(this.type, attributes);
      },

      /**
       * Update the code block at the current position. Primarily this is used
       * to change the language.
       *
       * ```ts
       * if (commands.updateCodeBlock.isEnabled()) {
       *   commands.updateCodeBlock({ language: 'markdown' });
       * }
       * ```
       */
      updateCodeBlock: attributes => updateNodeAttributes(this.type)(attributes),

      /**
       * Format the code block with the code formatting function passed as an
       * option.
       *
       * Code formatters (like prettier) add a lot to the bundle size and hence
       * it is up to you to provide a formatter which will be run on the entire
       * code block when this method is used.
       *
       * ```ts
       * if (actions.formatCodeBlock.isActive()) {
       *   actions.formatCodeBlockFactory();
       *   // Or with a specific position
       *   actions.formatCodeBlock({ pos: 100 }) // to format a separate code block
       * }
       * ```
       */
      formatCodeBlock: parameter => {
        return formatCodeBlockFactory({
          type: this.type,
          formatter: this.options.formatter,
          defaultLanguage: this.options.defaultLanguage
        })(parameter);
      }
    };
  }
  /**
   * Create an input rule that listens converts the code fence into a code block
   * when typing triple back tick followed by a space.
   */


  createInputRules() {
    var regexp = /^```([\dA-Za-z]*) $/;

    var getAttributes = match => {
      var language = getLanguage({
        language: getMatchString(match, 1),
        fallback: this.options.defaultLanguage
      });
      return {
        language
      };
    };

    return [nodeInputRule({
      regexp,
      type: this.type,
      beforeDispatch: (_ref2) => {
        var tr = _ref2.tr,
            start = _ref2.start;
        var $pos = tr.doc.resolve(start);
        tr.setSelection(new TextSelection($pos));
      },
      getAttributes: getAttributes
    })];
  }

  onSetOptions(parameter) {
    var changes = parameter.changes;

    if (changes.supportedLanguages) {
      this.registerLanguages();
    }
  }
  /**
   * Create specific keyboard bindings for the code block.
   */


  createKeymap() {
    return {
      Tab: (_ref3) => {
        var state = _ref3.state,
            dispatch = _ref3.dispatch;
        var selection = state.selection,
            tr = state.tr,
            schema = state.schema; // Check to ensure that this is the correct node.

        var _findNodeAtSelection = findNodeAtSelection(selection),
            node = _findNodeAtSelection.node;

        if (!isNodeOfType({
          node,
          types: this.type
        })) {
          return false;
        }

        if (selection.empty) {
          tr.insertText('\t');
        } else {
          var from = selection.from,
              to = selection.to;
          tr.replaceWith(from, to, schema.text('\t'));
        }

        if (dispatch) {
          dispatch(tr);
        }

        return true;
      },
      Backspace: (_ref4) => {
        var dispatch = _ref4.dispatch,
            tr = _ref4.tr,
            state = _ref4.state;
        var selection = tr.selection; // If the selection is not empty, return false and let other extension
        // (ie: BaseKeymapExtension) to do the deleting operation.

        if (!selection.empty) {
          return false;
        } // Check that this is the correct node.


        var parent = findParentNodeOfType({
          types: this.type,
          selection
        });

        if ((parent === null || parent === void 0 ? void 0 : parent.start) !== selection.from) {
          return false;
        }

        var pos = parent.pos,
            node = parent.node,
            start = parent.start;
        var toggleNode = state.schema.nodes[this.options.toggleName];

        if (node.textContent.trim() === '') {
          if (tr.doc.lastChild === node && tr.doc.firstChild === node) {
            replaceNodeAtPosition({
              pos,
              tr,
              content: toggleNode.create()
            });
          } else {
            removeNodeAtPosition({
              pos,
              tr
            });
          }
        } else if (start > 2) {
          // Jump to the previous node.
          tr.setSelection(TextSelection.create(tr.doc, start - 2));
        } else {
          // There is no content before the codeBlock so simply create a new
          // block and jump into it.
          tr.insert(0, toggleNode.create());
          tr.setSelection(TextSelection.create(tr.doc, 1));
        }

        if (dispatch) {
          dispatch(tr);
        }

        return true;
      },
      Enter: (_ref5) => {
        var dispatch = _ref5.dispatch,
            tr = _ref5.tr;
        var selection = tr.selection;

        if (!isTextSelection(selection) || !selection.$cursor) {
          return false;
        }

        var _selection$$from = selection.$from,
            nodeBefore = _selection$$from.nodeBefore,
            parent = _selection$$from.parent;

        if (!nodeBefore || !nodeBefore.isText || !parent.type.isTextblock) {
          return false;
        }

        var regex = /^```([A-Za-z]*)?$/;
        var text = nodeBefore.text;
        var textContent = parent.textContent;

        if (!text) {
          return false;
        }

        var matchesNodeBefore = text.match(regex);
        var matchesParent = textContent.match(regex);

        if (!matchesNodeBefore || !matchesParent) {
          return false;
        }

        var _matchesNodeBefore = _slicedToArray(matchesNodeBefore, 2),
            lang = _matchesNodeBefore[1];

        var language = getLanguage({
          language: lang,
          fallback: this.options.defaultLanguage
        });
        var pos = selection.$from.before();
        var end = pos + nodeBefore.nodeSize + 1; // +1 to account for the extra pos a node takes up

        tr.replaceWith(pos, end, this.type.create({
          language
        })); // Set the selection to within the codeBlock

        tr.setSelection(TextSelection.create(tr.doc, pos + 1));

        if (dispatch) {
          dispatch(tr);
        }

        return true;
      },
      [this.options.keyboardShortcut]: (_ref6) => {
        var tr = _ref6.tr;
        var commands = this.store.commands;

        if (!isNodeActive({
          type: this.type,
          state: tr
        })) {
          return false;
        }

        var enabled = commands.formatCodeBlock.isEnabled();

        if (enabled) {
          commands.formatCodeBlock();
        }

        return enabled;
      }
    };
  }
  /**
   * Create the custom code block plugin which handles the delete key amongst other things.
   */


  createPlugin() {
    var pluginState = new CodeBlockState(this.type, this);
    /**
     * Handles deletions within the plugin state.
     */

    var handler = () => {
      pluginState.setDeleted(true); // Delegate to the next key handler.

      return false;
    };

    return {
      state: {
        init(_, state) {
          return pluginState.init(state);
        },

        apply(tr) {
          return pluginState.apply(tr);
        }

      },
      props: {
        handleKeyDown: keydownHandler({
          Backspace: handler,
          'Mod-Backspace': handler,
          Delete: handler,
          'Mod-Delete': handler,
          'Ctrl-h': handler,
          'Alt-Backspace': handler,
          'Ctrl-d': handler,
          'Ctrl-Alt-Backspace': handler,
          'Alt-Delete': handler,
          'Alt-d': handler
        }),

        decorations() {
          pluginState.setDeleted(false);
          return pluginState.decorationSet;
        }

      }
    };
  }
  /**
   * Register passed in languages.
   */


  registerLanguages() {
    var _iterator = _createForOfIteratorHelper(this.options.supportedLanguages),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var language = _step.value;
        refractor.register(language);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  }

}, _temp)) || _class);

export { CodeBlockExtension, getLanguage };
